import { Badge } from '@ovComponents/2-component/badge/badge';
import { Chip } from '@ovComponents/2-component/chip/chip';
import { TableCell, TableRow } from '@ovComponents/2-component/table/table';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Box, Typography, useTheme } from 'hephaestus/web';
import { ExpandMore as ExpandMoreIcon } from 'hephaestus/webIcons';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReviewTradePortfolioGroup } from '../../types/tradeReview.types';
import { activateOnEnterOrSpace } from '../../utils/presentation/activateOnEnterOrSpace';
import {
  type ReviewTradeStatusFilter,
  resolveReviewTradeGroupDisplayAmounts,
  summarizeReviewTradeGroup,
  uniqueReviewTradeAccountTypeLabels,
} from '../../utils/tradeReview/tradeReviewPresentation';
import { ReviewTradeRowView } from './reviewTradeRowView';

export type ReviewTradeGroupRowsProps = {
  group: ReviewTradePortfolioGroup;
  expanded: boolean;
  showTradeColumns: boolean;
  onToggleExpanded: () => void;
  selectedIds: Set<string>;
  busy: boolean;
  canCancel: boolean;
  canSelect: boolean;
  onToggle: (id: string) => void;
  onCancelRow: (id: string) => void;
  localizedDate: (value: string) => string;
  showNoteColumn: boolean;
  statusFilter: ReviewTradeStatusFilter;
};

export const ReviewTradeGroupRows = ({
  group,
  expanded,
  showTradeColumns,
  onToggleExpanded,
  selectedIds,
  busy,
  canCancel,
  canSelect,
  onToggle,
  onCancelRow,
  localizedDate,
  showNoteColumn,
  statusFilter,
}: ReviewTradeGroupRowsProps) => {
  const { t } = useTranslation(['bulkRebalance']);
  const theme = useTheme();
  const tradesRegionId = useId();
  const summary = summarizeReviewTradeGroup(group.rows);
  const accountTypes = uniqueReviewTradeAccountTypeLabels(group.rows);
  const subtitleParts = [
    group.clientLabel ? group.portfolioLabel : null,
    group.modelLabel,
    t('reviewTrades.groupTradeCount', { count: summary.tradeCount }),
  ].filter(Boolean);
  const hideGroupMetrics = statusFilter !== 'all';
  const { buyCents: displayBuyCents, sellCents: displaySellCents } =
    resolveReviewTradeGroupDisplayAmounts(summary);
  const amountLines: Array<{
    key: string;
    label: string;
    amount: string;
    color: string;
  }> = [];
  if (!hideGroupMetrics && displayBuyCents > 0) {
    amountLines.push({
      key: 'buy',
      label: t('reviewTrades.type.BUY'),
      amount: formatMoneyValue(displayBuyCents),
      color: theme.color.positive,
    });
  }
  if (!hideGroupMetrics && displaySellCents > 0) {
    amountLines.push({
      key: 'sell',
      label: t('reviewTrades.type.SELL'),
      amount: formatMoneyValue(displaySellCents),
      color: theme.color.negative,
    });
  }

  const statusPills: Array<{
    key: string;
    label: string;
    color: 'warning' | 'positive' | 'neutral' | 'negative';
  }> = [];
  if (!hideGroupMetrics && !expanded) {
    if (summary.readyCount > 0) {
      statusPills.push({
        key: 'ready',
        label: t('reviewTrades.readyCount', { count: summary.readyCount }),
        color: 'positive',
      });
    }
    if (summary.heldCount > 0) {
      statusPills.push({
        key: 'held',
        label: t('reviewTrades.heldCount', { count: summary.heldCount }),
        color: 'warning',
      });
    }
    if (summary.pendingCount > 0) {
      statusPills.push({
        key: 'pending',
        label: t('reviewTrades.pendingCount', { count: summary.pendingCount }),
        color: 'neutral',
      });
    } else if (
      summary.readyCount === 0 &&
      summary.heldCount === 0 &&
      summary.canceledCount > 0
    ) {
      statusPills.push({
        key: 'canceled',
        label: t('reviewTrades.canceledCount', {
          count: summary.canceledCount,
        }),
        color: 'negative',
      });
    }
  }

  const identityCell = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        minWidth: 0,
      }}
    >
      <Typography variant='bodyMedium' sx={{ fontWeight: 600 }} noWrap>
        {group.clientLabel || group.portfolioLabel}
      </Typography>
      {subtitleParts.length > 0 ? (
        <Typography
          variant='labelSmall'
          color='textSecondary'
          noWrap
          title={subtitleParts.join(' · ')}
        >
          {subtitleParts.join(' · ')}
        </Typography>
      ) : null}
    </Box>
  );

  const sleevesCell = (
    <Box
      sx={{
        display: 'flex',
        gap: 0.5,
        flexWrap: 'wrap',
      }}
      data-testid={`bulk-rebalance-review-group-sleeves-${group.portfolioReportId}`}
    >
      {accountTypes.map((accountType) => (
        <Chip key={accountType} text={accountType} small />
      ))}
    </Box>
  );

  const amountCell = (
    <Box
      data-testid={`bulk-rebalance-review-group-amount-${group.portfolioReportId}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 0.25,
        whiteSpace: 'nowrap',
      }}
    >
      {amountLines.map((line) => (
        <Box
          key={line.key}
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'flex-end',
            gap: 0.75,
          }}
        >
          <Typography variant='bodySmall' color='textSecondary'>
            {line.label}
          </Typography>
          <Typography
            variant='bodySmall'
            sx={{
              color: line.color,
              fontVariantNumeric: 'tabular-nums',
              fontWeight: 600,
            }}
          >
            {line.amount}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const statusCell = (
    <Box
      data-testid={`bulk-rebalance-review-group-status-${group.portfolioReportId}`}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
      }}
    >
      {statusPills.map((pill) => (
        <Badge key={pill.key} label={pill.label} color={pill.color} small />
      ))}
    </Box>
  );

  return (
    <>
      <TableRow
        hover
        role='button'
        tabIndex={0}
        aria-expanded={expanded}
        aria-controls={
          expanded && group.rows.length > 0 ? tradesRegionId : undefined
        }
        aria-label={t('reviewTrades.toggleGroupAria', {
          name:
            group.clientLabel ||
            group.portfolioLabel ||
            t('reviewTrades.unknownPortfolio'),
        })}
        onClick={onToggleExpanded}
        onKeyDown={activateOnEnterOrSpace(onToggleExpanded)}
        data-testid={`bulk-rebalance-review-group-${group.portfolioReportId}`}
        sx={{
          cursor: 'pointer',
          bgcolor: theme.color.neutralHover,
        }}
      >
        <TableCell dense sx={{ width: 48 }}>
          <ExpandMoreIcon
            fontSize='small'
            aria-hidden
            sx={{
              display: 'block',
              color: theme.color.onSurfaceVariant,
              transform: expanded ? 'rotate(180deg)' : 'rotate(-90deg)',
              transition: 'transform 0.2s',
            }}
          />
        </TableCell>
        {showTradeColumns ? (
          <>
            <TableCell dense>{identityCell}</TableCell>
            <TableCell dense />
            <TableCell dense number>
              {amountCell}
            </TableCell>
            <TableCell dense />
            <TableCell dense />
            <TableCell dense>{sleevesCell}</TableCell>
            <TableCell dense>{statusCell}</TableCell>
            {showNoteColumn ? <TableCell dense /> : null}
            <TableCell dense />
          </>
        ) : (
          <>
            <TableCell dense>{identityCell}</TableCell>
            <TableCell dense>{sleevesCell}</TableCell>
            <TableCell dense number>
              {amountCell}
            </TableCell>
            <TableCell dense>{statusCell}</TableCell>
          </>
        )}
      </TableRow>
      {expanded &&
        group.rows.map((row, index) => (
          <ReviewTradeRowView
            key={row.id}
            row={row}
            regionId={index === 0 ? tradesRegionId : undefined}
            selected={selectedIds.has(row.id)}
            busy={busy}
            canCancel={canCancel}
            canSelect={canSelect}
            onToggle={onToggle}
            onCancelRow={onCancelRow}
            localizedDate={localizedDate}
            showNoteColumn={showNoteColumn}
          />
        ))}
    </>
  );
};
